import { Injectable } from '@angular/core';
import {AbstractControl, FormGroup, FormArray, FormControl, ValidatorFn, ValidationErrors, Validators} from '@angular/forms';
import { FormAccessor } from '../form-accessors/form-accessors';
import { states } from 'app/utilities/states';
import { getPostalCodeRegex } from 'app/utilities/countries';

@Injectable({
  providedIn: 'root'
})
export class FormValidator {
  constructor(private formAccessor: FormAccessor) {}

  isFormValid(form: FormGroup): boolean {
    return form.valid;
  }

  validateForm(form: FormGroup) {
    this.markAsTouchedAndDirty(form);
  }

  hasValidationErrors(field: AbstractControl | null): boolean {
    return !this.isFieldValid(field) && this.shouldDisplayFieldValidation(field);
  }

  isFieldValidAndShouldDisplayValidation(field: AbstractControl | null): boolean {
    return this.isFieldValid(field) && this.shouldDisplayFieldValidation(field);
  }

  isFieldInvalidAndShouldDisplayValidation(field: AbstractControl | null): boolean {
    return !this.isFieldValid(field) && this.shouldDisplayFieldValidation(field);
  }

  isFieldValid(field: AbstractControl | null): boolean {
    return this.formAccessor.isValid(field);
  }

  shouldDisplayFieldValidation(field: AbstractControl | null): boolean {
    if (this.formAccessor.hasValidator(field)) {
      return this.formAccessor.isTouched(field) &&
        !this.formAccessor.isPristine(field);
    } else {
      return this.formAccessor.isDirty(field);
    }
  }

  markAsTouchedAndDirty(group: FormGroup | FormArray): void {
    this.markGroupAsTouchedAndDirty(group);
    this.markControlsAsTouchedAndDirty(group);
  }

  markGroupAsTouchedAndDirty(group: FormGroup | FormArray): void {
    group.markAsDirty();
    group.markAsTouched();
  }

  markControlsAsTouchedAndDirty(group: FormGroup | FormArray) {
    for (const i in group.controls) {
      if (group.controls[i] instanceof FormControl) {
        group.controls[i].markAsTouched();
        group.controls[i].markAsDirty();
      } else {
        this.markAsTouchedAndDirty(group.controls[i]);
      }
    }
  }

  markFieldAsTouchedAndDirty(control: AbstractControl | null): void {
    if (!!control) {
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  static isPostalCodeRequiredForCountry(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const country = formGroup.get('country');
      const postalCode = formGroup.get('postalCode');
      if(!country || !postalCode) {
        return null;
      }

      if(country.value === 'USA') {
        return Validators.required(postalCode);
      }

      return null;
      
    }
  };

   public addOrRemoveCountrySpecificValidation(formGroup: AbstractControl) {
    if(!formGroup) {
      return;
    }

    const country = formGroup.get('country')?.value;
    let isUsa = country ? country === 'USA' : false;

    let stateControl = formGroup.get('state');
    let postalCodeControl = formGroup.get('postalCode');
    
    const stateValidators: ValidatorFn[] = [Validators.required];
    const postalCodeValidators: ValidatorFn[] = [Validators.required];
    
    // This code is here if we add the regex for foreign postal codes by using the country name when getting the regex.
    let postalCodeRegex = getPostalCodeRegex('USA');
     if(postalCodeRegex) {
      postalCodeValidators.push(Validators.pattern(postalCodeRegex));
     }

    if(isUsa) {
      stateControl?.addValidators(stateValidators);
      postalCodeControl?.addValidators(postalCodeValidators);
    } else {
      stateControl?.removeValidators(stateValidators)
      postalCodeControl?.removeValidators(postalCodeValidators);
    }

    stateControl?.updateValueAndValidity();
    postalCodeControl?.updateValueAndValidity();

    // Force re-validation
    setTimeout(() => {
      stateControl?.updateValueAndValidity();
      postalCodeControl?.updateValueAndValidity();
    });
  }

  static isRequiredForFormTypeCheck(control: AbstractControl, currentFormType: string, requiredFormTypes: string[]): ValidationErrors | null{
    if(!currentFormType || !requiredFormTypes?.length) {
          return null;
        }

        if(requiredFormTypes.includes(currentFormType)) {
          const value = control.value;
          
          // Ensure the value is defined.
          if(value === undefined || value === null) {
            return { required : true };
          }

          if(value && value instanceof Date) {
            return null;
          }

          // Ensure that a string array is valid.
          if(Array.isArray(value) && (value.length < 1 || !value[0].toString().replace(/\s/g, ''))) {
            return { required : true };
          }

          if(typeof value === 'string' && !value.toString().replace(/\s/g, '')) {
            return { required : true };
          }
        }
        
        return null;
  }

  // Allows for the form to have the Required validator be conditional based on the form type.
  static isRequiredForFormType(currentFormType: string, requiredFormTypes: string[]): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        return FormValidator.isRequiredForFormTypeCheck(control, currentFormType, requiredFormTypes);
    };
  }
  
  // Ensure parenthesis balance out and that we never have more right than left at a time.
  static validParenthesis(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(!control.value) {
        return null;
      }
    
      let parenCount = 0; //increment on left, decrement on right.
      const chars = [...control.value];
      for(let c of chars) {
        if(c === '(') {
          parenCount++;
        }

        if(c === ')') {
          parenCount--;
        }

        if(parenCount < 0) {
          return { nonStandardNotation: true };
        }
      }

      return parenCount === 0 ? null : { nonStandardNotation: true };
    }
  }
}